import React, { useState } from 'react';
import { X, Download, Upload, Clock, CheckCircle, AlertCircle, Loader, Calendar } from 'lucide-react';
import { downloadBackup } from '../lib/backup';
import { restoreFromFile, hasAutomaticBackup, restoreFromAutomaticBackup } from '../lib/restore';
import { addAuditLog } from '../lib/auditLog';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

interface BackupRestoreManagerProps {
  onClose: () => void;
}

export const BackupRestoreManager: React.FC<BackupRestoreManagerProps> = ({ onClose }) => {
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [backupFile, setBackupFile] = useState<File | null>(null);
  const [status, setStatus] = useState<{
    type: 'success' | 'error' | 'info' | null;
    message: string;
  }>({ type: null, message: '' });
  const [lastBackupTime, setLastBackupTime] = useState<string | null>(
    localStorage.getItem('installtrack_last_backup_time')
  );
  const [hasAutoBackup, setHasAutoBackup] = useState(hasAutomaticBackup());

  const handleCreateBackup = async () => {
    try {
      setIsCreatingBackup(true);
      setStatus({ type: 'info', message: 'Backup wird erstellt...' });
      
      await downloadBackup();
      
      setStatus({ type: 'success', message: 'Backup wurde erfolgreich erstellt und heruntergeladen.' });
      
      // Update last backup time
      const now = new Date().getTime().toString();
      localStorage.setItem('installtrack_last_backup_time', now);
      setLastBackupTime(now);
      
      // Log the backup creation
      addAuditLog('export', 'system', 'Manual backup created and downloaded');
    } catch (error) {
      console.error('Error creating backup:', error);
      setStatus({ 
        type: 'error', 
        message: error instanceof Error ? error.message : 'Fehler beim Erstellen des Backups' 
      });
    } finally {
      setIsCreatingBackup(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setBackupFile(file);
      setStatus({ type: 'info', message: `Datei "${file.name}" ausgewählt.` });
    }
  };

  const handleRestore = async () => {
    if (!backupFile) {
      setStatus({ type: 'error', message: 'Bitte wählen Sie eine Backup-Datei aus.' });
      return;
    }
    
    if (!window.confirm('Sind Sie sicher, dass Sie das System aus dem Backup wiederherstellen möchten? Alle aktuellen Daten werden überschrieben.')) {
      return;
    }
    
    try {
      setIsRestoring(true);
      setStatus({ type: 'info', message: 'System wird wiederhergestellt...' });
      
      await restoreFromFile(backupFile);
      
      setStatus({ type: 'success', message: 'System wurde erfolgreich wiederhergestellt.' });
      setBackupFile(null);
      
      // Reset file input
      const fileInput = document.getElementById('backup-file') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
      
      // Update auto backup status
      setHasAutoBackup(hasAutomaticBackup());
    } catch (error) {
      console.error('Error restoring from backup:', error);
      setStatus({ 
        type: 'error', 
        message: error instanceof Error ? error.message : 'Fehler bei der Wiederherstellung' 
      });
    } finally {
      setIsRestoring(false);
    }
  };

  const handleRestoreFromAutoBackup = async () => {
    if (!hasAutoBackup) {
      setStatus({ type: 'error', message: 'Kein automatisches Backup verfügbar.' });
      return;
    }
    
    if (!window.confirm('Sind Sie sicher, dass Sie das System aus dem automatischen Backup wiederherstellen möchten? Alle aktuellen Daten werden überschrieben.')) {
      return;
    }
    
    try {
      setIsRestoring(true);
      setStatus({ type: 'info', message: 'System wird aus automatischem Backup wiederhergestellt...' });
      
      await restoreFromAutomaticBackup();
      
      setStatus({ type: 'success', message: 'System wurde erfolgreich aus automatischem Backup wiederhergestellt.' });
    } catch (error) {
      console.error('Error restoring from automatic backup:', error);
      setStatus({ 
        type: 'error', 
        message: error instanceof Error ? error.message : 'Fehler bei der Wiederherstellung aus automatischem Backup' 
      });
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Download className="w-6 h-6 text-blue-600" />
            <span>Backup & Wiederherstellung</span>
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {status.type && (
          <div className={`mb-6 p-4 rounded-lg ${
            status.type === 'success' ? 'bg-green-50 text-green-700' :
            status.type === 'error' ? 'bg-red-50 text-red-700' :
            'bg-blue-50 text-blue-700'
          }`}>
            <div className="flex items-start">
              {status.type === 'success' && <CheckCircle className="w-5 h-5 mr-2 mt-0.5 shrink-0" />}
              {status.type === 'error' && <AlertCircle className="w-5 h-5 mr-2 mt-0.5 shrink-0" />}
              {status.type === 'info' && <Clock className="w-5 h-5 mr-2 mt-0.5 shrink-0" />}
              <p>{status.message}</p>
            </div>
          </div>
        )}

        <div className="space-y-8">
          {/* Backup section */}
          <div className="bg-blue-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-800 mb-4">Backup erstellen</h3>
            <p className="text-blue-700 mb-4">
              Erstellen Sie ein vollständiges Backup aller Daten. Das Backup wird als verschlüsselte Datei heruntergeladen.
            </p>
            
            {lastBackupTime && (
              <div className="flex items-center gap-2 mb-4 text-sm text-blue-700">
                <Calendar className="w-4 h-4 shrink-0" />
                <span>
                  Letztes Backup: {format(new Date(parseInt(lastBackupTime)), 'dd. MMMM yyyy, HH:mm', { locale: de })}
                </span>
              </div>
            )}
            
            <button
              onClick={handleCreateBackup}
              disabled={isCreatingBackup}
              className="w-full flex justify-center items-center gap-2 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
            >
              {isCreatingBackup ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Backup wird erstellt...</span>
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  <span>Backup erstellen und herunterladen</span>
                </>
              )}
            </button>
          </div>
          
          {/* Restore section */}
          <div className="bg-amber-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-amber-800 mb-4">Wiederherstellung</h3>
            <p className="text-amber-700 mb-4">
              Stellen Sie das System aus einem Backup wieder her. Alle aktuellen Daten werden überschrieben.
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-amber-700 mb-2">
                Backup-Datei auswählen
              </label>
              <input
                type="file"
                id="backup-file"
                accept=".itpb"
                onChange={handleFileChange}
                className="block w-full text-sm text-amber-700
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-medium
                  file:bg-amber-600 file:text-white
                  hover:file:bg-amber-700
                  file:cursor-pointer"
                disabled={isRestoring}
              />
            </div>
            
            <button
              onClick={handleRestore}
              disabled={!backupFile || isRestoring}
              className="w-full flex justify-center items-center gap-2 py-2 px-4 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-amber-300 disabled:cursor-not-allowed mb-4"
            >
              {isRestoring ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Wiederherstellung läuft...</span>
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  <span>Aus Backup wiederherstellen</span>
                </>
              )}
            </button>
            
            {hasAutoBackup && (
              <>
                <div className="border-t border-amber-200 my-4 pt-4">
                  <h4 className="text-md font-medium text-amber-800 mb-2">Automatisches Backup</h4>
                  <p className="text-sm text-amber-700 mb-4">
                    Es ist ein automatisches Backup verfügbar. Sie können das System auf den letzten automatisch gesicherten Zustand zurücksetzen.
                  </p>
                  
                  <button
                    onClick={handleRestoreFromAutoBackup}
                    disabled={isRestoring}
                    className="w-full flex justify-center items-center gap-2 py-2 px-4 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:bg-amber-300 disabled:cursor-not-allowed"
                  >
                    {isRestoring ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        <span>Wiederherstellung läuft...</span>
                      </>
                    ) : (
                      <>
                        <Clock className="w-5 h-5" />
                        <span>Aus automatischem Backup wiederherstellen</span>
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
};